import { Component, OnDestroy, OnInit, ViewChild, Input } from '@angular/core';
import { ScannerService } from '../../../services/scanner.service';
import { BarcodeFormat } from '@zxing/library';
import { ActivatedRoute, Router } from '@angular/router';
import { ZXingScannerComponent } from '@zxing/ngx-scanner';
import { StorageService } from 'src/app/services/storage.service';
import { Camera, CameraDirection } from '@capacitor/camera';
import { Capacitor } from '@capacitor/core';

// Only import sensors for Android
let sensorsModule: any = null;
let sensorType: any = null;
let sensorDelay: any = null;
if (Capacitor.getPlatform() === 'android') {
  try {
    const {
      Sensors: sensors,
      SensorType: sensorsType,
      SensorDelay: sensorsDelay,
    } = require('@danyalwe/capacitor-sensors');
    sensorsModule = sensors;
    sensorType = sensorsType;
    sensorDelay = sensorsDelay;
  } catch (error) {
    console.error('Failed to load capacitor-sensors module:', error);
  }
}

@Component({
  selector: 'app-scanner',
  templateUrl: './scanner.component.html',
  styleUrls: ['./scanner.component.scss'],
})
export class ScannerComponent implements OnInit, OnDestroy {
  cameras: any[] = [];

  public visitId: string;
  public date: string;
  public dayNumber: string;
  public areaId: string;
  public devices: MediaDeviceInfo[];
  public scannerEnabled = true;
  public tryHarderOption = false;
  threshold = 10;
  lightIntensity: number | null = null;
  torchlightEnabled = false;
  autofocus = true;
  currentDevice: MediaDeviceInfo = null;
  videoConstraints: MediaTrackConstraints = {
    width: { min: 800, ideal: 1920 },
    height: { min: 450, ideal: 1080 },
    aspectRatio: { ideal: 1.7777777778 },
  };
  sensor: any = null;
  sensorInitialized = false;

  @ViewChild('scanner', { static: false })
  scanner: ZXingScannerComponent;

  allowedFormats = [
    BarcodeFormat.CODE_39,
    BarcodeFormat.CODE_93,
    BarcodeFormat.CODE_128,
    BarcodeFormat.EAN_8,
    BarcodeFormat.EAN_13,
    BarcodeFormat.QR_CODE,
  ];
  private instanceId: string;

  constructor(
    private route: ActivatedRoute,
    private scannerService: ScannerService,
    private router: Router,
    private localStorage: StorageService
  ) {}

  async ngOnInit() {
    this.localStorage.get('tryHarderOption').then((val) => {
      if (val) {
        this.tryHarderOption = val === 'true';
      }
    });

    this.route.params.subscribe((params) => {
      this.instanceId = params.instanceId;
      this.visitId = params?.id;
      this.date = params?.date;
      this.dayNumber = params?.dayId;
      this.areaId = params?.areaid;
    });

    if (Capacitor.getPlatform() === 'android' && sensorsModule) {
      try {
        await this.startLightSensor();
      } catch (error) {
        console.error('Error initializing light sensor:', error);
        // Continue without the light sensor
      }
    }
  }

  async ngOnDestroy() {
    if (this.scanner) {
      this.scanner.tryHarder = false;
      this.scanner.scanStop();
    }
    this.scannerEnabled = false;
    if (
      Capacitor.getPlatform() === 'android' &&
      sensorsModule &&
      this.sensorInitialized
    ) {
      try {
        await sensorsModule.removeAllListeners();
        if (this.sensor) {
          await sensorsModule.stop(this.sensor);
          this.sensorInitialized = false;
        }
      } catch (error) {
        console.error('Error cleaning up sensors:', error);
      }
    }
    this.torchlightEnabled = false;
  }

  async startLightSensor() {
    if (Capacitor.getPlatform() !== 'android' || !sensorsModule) {
      return;
    }

    try {
      this.sensor = await sensorsModule.init({
        type: sensorType.AMBIENT_LIGHT,
        delay: sensorDelay.NORMAL,
      });

      await sensorsModule.addListener('AMBIENT_LIGHT', (data) => {
        if (data && data.values && data.values.length > 0) {
          this.lightIntensity = data.values[0];
          this.handleLightChange();
        }
      });
      this.sensorInitialized = true;
      await sensorsModule.start(this.sensor);
    } catch (error) {
      console.error('Error starting light sensor:', error);
    }
  }

  async handleLightChange() {
    if (
      this.lightIntensity === null ||
      Capacitor.getPlatform() !== 'android' ||
      !this.scanner
    ) {
      return;
    }

    try {
      if (this.lightIntensity < this.threshold && !this.scanner.torch) {
        this.scanner.torch = true;
        this.torchlightEnabled = true;
      }
    } catch (error) {
      console.error('Error handling light change:', error);
    }
  }

  onScanSuccess(result: string): void {
    if (this.scanner) {
      this.scanner.tryHarder = false;
    }
    this.scannerService.sendScanResult(this.instanceId, result);

    if (this.instanceId === 'area') {
      this.router.navigate([
        'area',
        this.date,
        this.visitId,
        this.dayNumber,
        this.areaId,
      ]);
    }
  }

  onCamerasFound(devices: MediaDeviceInfo[]): void {
    const cameraDevices = devices.filter((d) => d.kind === 'videoinput');

    const rearCameras = cameraDevices.filter(
      (device) =>
        device.label.toLowerCase().includes('back') ||
        device.label.toLowerCase().includes('rear')
    );

    this.devices = rearCameras.length > 0 ? rearCameras : cameraDevices;
    setTimeout(() => {
      this.localStorage.get('defaultCamera').then((device) => {
        device = JSON.parse(device);
        if (device) {
          this.currentDevice = devices.find((d) => d.deviceId === device);
          if (!this.currentDevice) {
            this.localStorage.store(
              'defaultCamera',
              devices[devices.length - 1].deviceId
            );
            this.currentDevice = devices[devices.length - 1];
          }
        } else {
          this.localStorage.store(
            'defaultCamera',
            devices[devices.length - 1].deviceId
          );
          this.currentDevice = devices[devices.length - 1];
        }
      });
    }, 1000);
  }

  back(): void {
    if (this.instanceId === 'area') {
      this.router.navigate([
        'area',
        this.date,
        this.visitId,
        this.dayNumber,
        this.areaId,
      ]);
    }
  }

  switchAutoFocus(): void {
    this.autofocus = !this.autofocus;
  }
  onCameraSelect(): void {
    this.localStorage.store('defaultCamera', this.currentDevice.deviceId);
  }

  onFlashlightClick() {
    if (this.torchlightEnabled) {
      this.torchlightEnabled = false;
    } else {
      this.torchlightEnabled = true;
    }
    if (this.scanner) {
      this.scanner.torch = this.torchlightEnabled;
    }
  }
}
